<template>
  <div class="payment-page">
    <div class="container">
      <h1>Payment</h1>
      
      <div class="payment-grid">
        <!-- Payment Options -->
        <div class="payment-section">
          <h2>Select Payment Method</h2>
          
          <div v-if="paymentMethods.length === 0" class="no-payment-methods">
            <p>No payment methods available. Please contact support.</p>
          </div>
          <div v-else class="payment-methods">
            <label 
              v-for="method in paymentMethods" 
              :key="method.code"
              class="payment-method" 
              :class="{ active: selectedMethod === method.code }"
            >
              <input type="radio" v-model="selectedMethod" :value="method.code">
              <div class="method-info">
                <i :class="getPaymentMethodIcon(method)"></i>
                <div>
                  <h3>{{ method.name }}</h3>
                  <p>{{ method.description }}</p>
                </div>
              </div>
            </label>
          </div>
          
          <!-- Bank Transfer Instructions (shown when bank_transfer is selected) -->
          <div v-if="selectedMethodDetails && selectedMethodDetails.type === 'manual'" class="bank-transfer-info">
            <h3><i class="fas fa-info-circle"></i> Bank Transfer Instructions</h3>
            <div class="bank-details">
              <div v-if="selectedMethodDetails.bank_name" class="detail-row">
                <span>Bank:</span>
                <strong>{{ selectedMethodDetails.bank_name }}</strong>
              </div>
              <div v-if="selectedMethodDetails.account_name" class="detail-row">
                <span>Account Name:</span>
                <strong>{{ selectedMethodDetails.account_name }}</strong>
              </div>
              <div v-if="selectedMethodDetails.account_number" class="detail-row">
                <span>Account Number:</span>
                <strong>{{ selectedMethodDetails.account_number }}</strong>
                <button @click="copyAccountNumber" class="btn-copy" title="Copy">
                  <i class="fas fa-copy"></i>
                </button>
              </div>
              <div class="detail-row amount">
                <span>Amount to Transfer:</span>
                <strong>₦{{ formatPrice(order.total) }}</strong>
              </div>
              <div class="detail-row reference">
                <span>Payment Reference:</span>
                <strong>{{ order.number }}</strong>
              </div>
            </div>
            <p class="instructions" v-if="selectedMethodDetails.instructions">{{ selectedMethodDetails.instructions }}</p>
          </div>

          <button @click="handlePayment" class="btn-pay" :disabled="!selectedMethod || loading">
            <i v-if="loading" class="fas fa-spinner fa-spin"></i>
            <i v-else class="fas fa-lock"></i>
            {{ loading ? 'Processing Payment...' : `Pay ₦${formatPrice(order.total)}` }}
          </button>
          
          <p class="payment-notice">
            <i class="fas fa-shield-alt"></i>
            Your payment is secure and encrypted
          </p>
        </div>

        <!-- Order Summary -->
        <div class="order-info">
          <h2>Order Summary</h2>
          
          <div class="info-row">
            <span>Order Number:</span>
            <span class="order-number">#{{ order.number }}</span>
          </div>

          <div class="order-items">
            <h3>Items ({{ order.order_items?.length || 0 }})</h3>
            <div v-for="item in order.order_items" :key="item.id" class="order-item">
              <div>
                <p>{{ item.product_name }}</p>
                <small>Qty: {{ item.quantity }}</small>
              </div>
              <span>₦{{ formatPrice(item.price * item.quantity) }}</span>
            </div>
          </div>

          <div class="order-totals">
            <div class="total-row">
              <span>Subtotal</span>
              <span>₦{{ formatPrice(order.subtotal || order.total) }}</span>
            </div>
            <div v-if="order.shipping_cost && order.shipping_cost > 0" class="total-row">
              <span>Shipping</span>
              <span>₦{{ formatPrice(order.shipping_cost) }}</span>
            </div>
            <div v-if="order.discount_amount && order.discount_amount > 0" class="total-row discount">
              <span>Discount</span>
              <span>-₦{{ formatPrice(order.discount_amount) }}</span>
            </div>
            <div class="total-row grand-total">
              <span>Total</span>
              <span>₦{{ formatPrice(order.total) }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Payment',
  props: {
    order: {
      type: Object,
      required: true
    },
    paymentMethods: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      selectedMethod: this.paymentMethods.length > 0 ? this.paymentMethods[0].code : '',
      loading: false
    }
  },
  computed: {
    selectedMethodDetails() {
      return this.paymentMethods.find(m => m.code === this.selectedMethod)
    }
  },
  methods: {
    formatPrice(price) {
      return new Intl.NumberFormat('en-NG').format(price)
    },
    
    getPaymentMethodIcon(method) {
      const icons = {
        'paystack': 'fas fa-credit-card',
        'flutterwave': 'fas fa-wallet',
        'bank_transfer': 'fas fa-university',
        'stripe': 'fab fa-stripe'
      }
      
      if (icons[method.code]) return icons[method.code]
      if (method.type === 'manual') return 'fas fa-money-bill-wave'
      return 'fas fa-credit-card'
    },
    
    copyAccountNumber() {
      const accountNumber = this.selectedMethodDetails?.account_number
      if (accountNumber) {
        navigator.clipboard.writeText(accountNumber)
        if (window.Swal) {
          window.Swal.fire({
            icon: 'success',
            title: 'Copied!',
            text: 'Account number copied to clipboard',
            timer: 1500,
            showConfirmButton: false,
            position: 'top-end',
            toast: true
          })
        }
      }
    },
    
    async handlePayment() {
      this.loading = true
      
      try {
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
        
        const response = await fetch(`/orders/${this.order.number}/pay`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': csrfToken,
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            payment_method: this.selectedMethod
          })
        })
        
        const data = await response.json()
        
        if (response.ok) {
          // Handle different payment types
          if (data.type === 'manual') {
            // Manual payment (bank transfer) - show success with instructions
            if (window.Swal) {
              window.Swal.fire({
                icon: 'success',
                title: 'Order Confirmed!',
                html: `<p>${data.message}</p><p>Please complete your bank transfer to confirm your order.</p>`,
                confirmButtonText: 'View Order',
                confirmButtonColor: '#0063d1'
              }).then(() => {
                window.location.href = `/orders/${this.order.number}/success`
              })
            } else {
              window.location.href = `/orders/${this.order.number}/success`
            }
          } else {
            // Gateway payment - redirect to payment URL
            if (window.Swal) {
              window.Swal.fire({
                icon: 'info',
                title: 'Redirecting to Payment Gateway',
                text: 'Please wait...',
                timer: 2000,
                showConfirmButton: false,
                allowOutsideClick: false
              })
            }
            setTimeout(() => {
              window.location.href = data.authorization_url || data.payment_url
            }, 2000)
          }
        } else {
          if (window.Swal) {
            window.Swal.fire({
              icon: 'error',
              title: 'Payment Error',
              text: data.message || 'Payment initialization failed',
              confirmButtonColor: '#0063d1'
            })
          } else {
            alert(data.message || 'Payment initialization failed')
          }
        }
      } catch (error) {
        if (window.Swal) {
          window.Swal.fire({
            icon: 'error',
            title: 'Error',
            text: 'An error occurred. Please try again.',
            confirmButtonColor: '#0063d1'
          })
        } else {
          alert('An error occurred. Please try again.')
        }
      } finally {
        this.loading = false
      }
    }
  }
}
</script>
